import React, { useState, useCallback } from 'react';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { ChevronDown, ChevronUp, User, Settings, LogOut, Building2 } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { createPortal } from 'react-dom';
import { cn } from '@/utils/cn';

import Profile from './components/ProfileEditModal';
import * as actions from '@/store/actions/auth';
import Profiles from './components/Profiles';

function CurrentItem({ title, role, toggleOpen, open }) {
  return (
    <button
      onClick={toggleOpen}
      data-testid="profile-popover-toggle-btn"
      className="w-full flex items-center justify-between px-4 py-2.5 border-t border-neutral-150 hover:bg-neutral-50 transition-colors"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="h-8 w-8 rounded bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
          <Building2 size={14} />
        </div>
        <div className="min-w-0 text-left">
          <div className="text-xs text-neutral-500">{role}</div>
          <div className="text-sm font-medium text-neutral-900 truncate">{title}</div>
        </div>
      </div>
      {open ? (
        <ChevronUp size={14} className="text-neutral-400 shrink-0" />
      ) : (
        <ChevronDown size={14} className="text-neutral-400 shrink-0" />
      )}
    </button>
  );
}

const PureProfileDialog = ({ visible, onClose, history }) => {
  const [settings, setSettings] = useState(false);

  const { first_name, last_name, email, image_url } = useSelector((state) => state.auth.user);
  const { employees, current_employee } = useSelector((state) => state.auth);
  const { loadingMenu } = useSelector((state) => state.menu);
  const [open, setOpen] = useState(loadingMenu);

  const dispatch = useDispatch();

  const toggleOpen = () => setOpen(!open);
  const changeSettings = () => setSettings(!settings);
  const findCurrentEmployee = () => employees.find((ele) => ele.id === current_employee);

  useCallback(() => {
    const employee = findCurrentEmployee();
    if (employee && (employee as any).id !== current_employee) {
      dispatch(actions.currentCompanyChanged(employee.id, employee.company.id));
    }
  }, [current_employee]);

  const renderList = () => {
    let first: any = {};
    const user = {
      company: { title: `${first_name} ${last_name}` },
      role: { id: null, title: 'Uživatel' },
      id: null,
    };

    if (Array.isArray(employees) && employees.length === 0) {
      first = user;
    } else if (current_employee) {
      first = findCurrentEmployee();
    } else {
      first = user;
    }

    return (
      <>
        <CurrentItem
          title={first?.company.title}
          role={first?.role.title}
          open={open}
          toggleOpen={toggleOpen}
        />
        {open && (
          <Profiles employees={[...employees, user]} toggleOpen={toggleOpen} history={history} />
        )}
      </>
    );
  };

  const handleClose = () => {
    if (loadingMenu) return;
    onClose();
  };

  const initials = `${first_name?.[0] || ''}${last_name?.[0] || ''}`.toUpperCase() || 'U';

  return (
    <>
      {createPortal(
        <div
          className={cn(
            'fixed inset-0 z-[9998] transition-colors duration-300',
            visible ? 'bg-black/20' : 'pointer-events-none'
          )}
          onClick={handleClose}
        >
          {loadingMenu && (
            <div className="h-full w-full flex justify-center items-center gap-4">
              <SpinnerOverlay />
              <span className="text-sm text-neutral-700">Data se načítají...</span>
            </div>
          )}
          <div
            data-testid="profile-popover"
            className={cn(
              'absolute left-[76px] w-[320px] bg-white rounded-lg shadow-pop border border-neutral-200 flex flex-col overflow-hidden transition-all duration-300',
              visible ? 'ease-out bottom-4 opacity-100' : 'ease-in -bottom-full opacity-0 invisible'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {/* User info header */}
            <div className="flex items-center gap-3 p-4">
              <div className="shrink-0">
                {image_url ? (
                  <img src={image_url} className="h-11 w-11 rounded-full object-cover" />
                ) : (
                  <div className="h-11 w-11 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-semibold">
                    {initials}
                  </div>
                )}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-neutral-900 truncate">{`${first_name} ${last_name}`}</div>
                <div className="text-xs text-neutral-500 truncate">{email}</div>
              </div>
            </div>

            {/* Company/role selector */}
            {renderList()}

            {/* Actions */}
            <div className="border-t border-neutral-150 flex">
              <button
                data-testid="profile-popover-manage-btn"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50 transition-colors"
                onClick={() => setSettings(true)}
              >
                <Settings size={13} className="text-neutral-500" />
                Spravovat profil
              </button>
              <div className="w-px bg-neutral-150" />
              <button
                data-testid="profile-popover-logout-btn"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                onClick={() => dispatch(actions.logoutUser())}
              >
                <LogOut size={13} />
                Odhlásit se
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      <Profile visible={settings} onClose={changeSettings} onSubmit={() => setSettings(false)} />
    </>
  );
};

function ProfilePopover({ history }) {
  const { image_url, first_name, last_name } = useSelector((state) => state.auth.user);
  const { loadingMenu } = useSelector((state) => state.menu);
  const [visible, setVisible] = useState(loadingMenu);

  const handleProfileDialogVisible = () => {
    if (loadingMenu) return;
    setVisible((v) => !v);
  };

  const initials = `${first_name?.[0] || ''}${last_name?.[0] || ''}`.toUpperCase() || 'U';

  return (
    <>
      <button
        data-testid="profile-popover-avatar-btn"
        onClick={handleProfileDialogVisible}
        className="h-9 w-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white text-[11px] font-semibold transition-colors overflow-hidden"
      >
        {image_url ? <img src={image_url} className="h-full w-full object-cover" /> : initials}
      </button>
      <PureProfileDialog history={history} visible={visible} onClose={handleProfileDialogVisible} />
    </>
  );
}

export default ProfilePopover;
